import type { PublicationModel } from "../../types/models";

type DestinationAccount = { id: string; provider: string };

/** Persisted posts define destination intent. Only destination fields are
 * accepted here: status governs behavior elsewhere, never default inference.
 * The session pool is the union of participating connected accounts. Only a blank
 * session uses connected-account defaults. */
export function inferSessionAccountPool({
  videoIds,
  accounts,
  publications,
}: {
  videoIds: readonly string[];
  accounts: readonly DestinationAccount[];
  publications: readonly Pick<PublicationModel, "projectId" | "accountId">[];
}): string[] {
  const sessionIds = new Set(videoIds);
  const participatingAccounts = new Set<string>();

  for (const publication of publications) {
    if (!publication.projectId || !sessionIds.has(publication.projectId)) continue;
    participatingAccounts.add(publication.accountId);
  }

  // Stable IDs, rather than query order, make repeated openings predictable.
  const stableAccounts = [...accounts].sort((a, b) => a.id.localeCompare(b.id));
  if (participatingAccounts.size > 0) {
    return stableAccounts.filter((account) => participatingAccounts.has(account.id))
      .map((account) => account.id);
  }

  const chosenProviders = new Set<string>();
  return stableAccounts.filter((account) => {
    if (chosenProviders.has(account.provider)) return false;
    chosenProviders.add(account.provider);
    return true;
  }).map((account) => account.id);
}

export function stableValue(value: unknown): unknown {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.toISOString() : null;
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
      .map(([key, child]) => [key, stableValue(child)]));
  }
  return value;
}
